globalThis.ebizTimePicker = globalThis.ebizTimePicker || {};

globalThis.ebizTimePicker.disableWheelOnInput = function () {
    const wrapper = document.querySelector('.ebiz-time-picker-wrapper');
    if (!wrapper) return;

    const input = wrapper.querySelector('.k-input-inner');
    if (!input) return;

    input.addEventListener(
        'wheel',
        function (e) {
            e.preventDefault();
            e.stopPropagation();
        },
        { passive: false }
    );
};

// checks to see if popup is still visible to keep open stylings synced
globalThis.ebizTimePicker.registerPopupObserver = function (dotNetRef) {
    function attachObserver() {
        const popup = document.querySelector('.ebiz-time-picker-popup');
        if (!popup) {
            requestAnimationFrame(attachObserver);
            return;
        }

        if (popup._ebizObserverAttached) {
            return;
        }
        popup._ebizObserverAttached = true;

        const isVisible = () => {
            const hasShownClass = popup.classList.contains('k-animation-container-shown');
            const displayNotNone = globalThis.getComputedStyle(popup).display !== 'none';
            return hasShownClass && displayNotNone;
        };

        let lastVisible = isVisible();

        const observer = new MutationObserver(() => {
            const nowVisible = isVisible();

            if (lastVisible && !nowVisible) {
                dotNetRef.invokeMethodAsync('CloseFromDom');
            }

            lastVisible = nowVisible;
        });

        observer.observe(popup, {
            attributes: true,
            attributeFilter: ['class', 'style']
        });

        popup._ebizObserver = observer;
    }

    attachObserver();
};

// Detects clicks on the popup's Apply button so we can route the close through the
// "focus the input immediately" path instead of the blur loop below.
globalThis.ebizTimePicker.attachApplyFocus = function () {
    if (globalThis.ebizTimePicker._applyFocusAttached) return;
    globalThis.ebizTimePicker._applyFocusAttached = true;

    // Delegated capture listener: the Apply button is recreated each time the popup opens.
    document.addEventListener(
        "mousedown",
        (e) => {
            const accept = e.target.closest && e.target.closest(".ebiz-time-picker-popup .k-time-accept");
            if (!accept) return;
            globalThis.ebizTimePicker._closeFromApply = true;
            setTimeout(() => (globalThis.ebizTimePicker._closeFromApply = false), 250);
        },
        { capture: true }
    );
};

globalThis.ebizTimePicker.unhighlightInput = function () {
    // Closing via Apply: return focus to the input and highlight the HH portion
    // immediately, skipping the blur loop so there's no perceived delay.
    if (globalThis.ebizTimePicker._closeFromApply) {
        const input = document.querySelector(".ebiz-time-picker .k-input-inner");
        if (input) {
            input.focus();
            try {
                input.setSelectionRange(0, 2);
            } catch {
                console.log("An error occurred while trying to set the selection range on the HH of the input.");
            }
        }
        return;
    }

    const start = performance.now();

    const overrideTelerikFocusOnInput = () => {

        const input = document.activeElement;

        // if clicking input, focus the HH portion of input
        if (globalThis.ebizTimePicker._closeFromInput && input?.tagName === "INPUT") {
            try {
                input.setSelectionRange(0, 2);
            } catch {
                console.log("An error occurred while trying to set the selection range on the HH of the input.");
            }
            return;
        }

        // if not clicking input, blur off the input
        if (input && input.tagName === "INPUT") {
            try {
                const length = (input.value || "").length;
                input.setSelectionRange(length, length);
            }
            catch {
                console.log("An error occurred while trying to set the selection range on the input.");
            }
        }

        // Blur/Focus off input
        input?.blur?.();
        document.body.focus?.();

        // Run for long enough Telerik's focus method finished running
        if (performance.now() - start < 200) {
            requestAnimationFrame(overrideTelerikFocusOnInput);
        }
    };

    requestAnimationFrame(overrideTelerikFocusOnInput);
};

globalThis.ebizTimePicker.formatTimeNumbers = function () {
    const addLeadingZero = () => {
        let timeValues = document.querySelectorAll('.k-time-list[aria-label="Hour"] span, .k-time-list[aria-label="Minute"] span');
        timeValues.forEach(s => {
            const v = s.textContent.trim();
            if (/^\d$/.test(v)) s.textContent = v.padStart(2, "0");
        });
    };

    const end = performance.now() + 300;
    (function tick() {
        addLeadingZero();
        if (performance.now() < end) requestAnimationFrame(tick);
    })();
};

globalThis.ebizTimePicker.attachHourFocus = function () {
    const input = document.querySelector(".ebiz-time-picker .k-input-inner");
    if (!input || input.__hourFocusAttached) return;

    input.__hourFocusAttached = true;

    input.addEventListener("mousedown", () => {
        globalThis.ebizTimePicker._closeFromInput = true;
        setTimeout(() => (globalThis.ebizTimePicker._closeFromInput = false), 250);
    });

    const selectHour = () => {
        requestAnimationFrame(() => {
            try {
                input.setSelectionRange(0, 2);
            } catch {
                console.log("An error occurred while trying to set the selection range on the HH of the input.");
            }
        });
    };

    input.addEventListener("focus", selectHour);
    input.addEventListener("click", selectHour);
};

// Tames mouse-wheel/trackpad scrolling inside the popup time lists (Hour/Minute/AM-PM).
// Telerik's native kinetic scroller amplifies a single wheel notch into a large momentum
// scroll that overshoots and lands on the wrong entry (jumping past by ~2 items). We take
// over the wheel: each notch advances exactly one item by clicking the targeted list item,
// which both moves the list and updates Telerik's selected value cleanly (no momentum).
globalThis.ebizTimePicker.tamePopupScroll = function () {
    if (globalThis.ebizTimePicker._popupScrollTamed) return;
    globalThis.ebizTimePicker._popupScrollTamed = true;

    // Accumulated wheel delta per container, so small trackpad deltas still advance an item.
    const accumulated = new WeakMap();
    const STEP_DELTA = 16; // px of accumulated deltaY needed to advance one item

    // Index of the item currently aligned with the popup's center highlight bar.
    const centeredItemIndex = function (container) {
        const items = [...container.querySelectorAll("li")];
        const listContainer = container.closest(".k-time-list-container");
        const highlight = listContainer && listContainer.querySelector(".k-time-highlight");
        if (!highlight || items.length === 0) return { items, index: 0 };

        const highlightRect = highlight.getBoundingClientRect();
        const center = highlightRect.top + highlightRect.height / 2;

        let bestIndex = 0;
        let bestDistance = Infinity;
        items.forEach((item, i) => {
            const rect = item.getBoundingClientRect();
            const distance = Math.abs(rect.top + rect.height / 2 - center);
            if (distance < bestDistance) {
                bestDistance = distance;
                bestIndex = i;
            }
        });
        return { items, index: bestIndex };
    };

    // Capture phase on window so we run before Telerik's own wheel handler and can cancel it.
    globalThis.addEventListener(
        "wheel",
        function (e) {
            const container = e.target.closest && e.target.closest(".k-time-container");
            if (!container || !container.closest(".ebiz-time-picker-popup")) return;

            e.preventDefault();
            e.stopImmediatePropagation();

            // Normalize line-mode deltas (deltaMode === 1) to roughly pixel scale.
            const delta = e.deltaY * (e.deltaMode === 1 ? 16 : 1);
            let total = (accumulated.get(container) || 0) + delta;

            let step = 0;
            if (Math.abs(total) >= STEP_DELTA) {
                step = Math.sign(total); // one item per notch, never overshoot
                total = 0;
            }
            accumulated.set(container, total);
            if (step === 0) return;

            const { items, index } = centeredItemIndex(container);
            const target = Math.max(0, Math.min(items.length - 1, index + step));
            if (target !== index) {
                items[target].click();
            }
        },
        { capture: true, passive: false }
    );
};

globalThis.ebizTimePicker.disableInputDragScroll = function () {
    const input = document.querySelector(".ebiz-time-picker .k-input-inner");
    if (!input || input.__dragDisabled) return;
    input.__dragDisabled = true;

    input.addEventListener("mousedown", (e) => {
        if (e.button !== 0) return
        e.preventDefault();
        input.focus();
    });

    input.addEventListener("dragstart", (e) => e.preventDefault());
};